import { BilledToOwnerNotice } from '@components/ChooseMediaModal/BilledToOwnerNotice';
import { ChooseFileUploadModal } from '@components/ChooseMediaModal/ChooseFileUploadModal';
import { ChooseFileUploadModalSummaryConfirm } from '@components/ChooseMediaModal/ChooseFileUploadModalSummaryConfirm';
import { ContactUsLink } from '@components/ContactUsLink/ContactUsLink';
import UploadDocumentsIcon from '@components/Icons/UploadDocumentsIcon';
import { SupportedPlatformsText } from '@components/SupportedPlatformsText/SupportedPlatformsText';
import { URLInput } from '@components/URLInput/URLInput';
import { DragDropRejected, getMediaAcceptAttribute } from '@lib/files/drag-files';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { useDragDropUpload } from '@lib/hooks/useDragDropUpload';
import { useFileUploadFlow } from '@lib/hooks/useFileUploadFlow';
import { useOwnerAffordability } from '@lib/hooks/useOwnerAffordability';
import { useTopUp } from '@lib/hooks/useTopUp';
import { FOLDER_ROOT_PATH, type FolderPath } from '@lib/library/folderPath';
import {
  ACCEPTED_AUDIO_FILE_EXTENSIONS,
  ACCEPTED_IMAGE_FILE_EXTENSIONS,
  ACCEPTED_VIDEO_FILE_EXTENSIONS,
} from '@lib/utils/fileExtensions';
import { estimateIngest } from '@lib/utils/ingestEstimate';
import { ownerShortfallBlock } from '@lib/utils/ownerAffordability';
import { getLibraryUrl } from '@lib/utils/url';
import { ActionButton } from '@pages/ActionButton';
import { DEFAULT_LIBRARY_NAME } from '@pages/Libraries/components/LibraryCreateModal';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { useUploadURL } from '@stores/UploadURL/UploadURLStore';
import classNames from 'classnames';
import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router';
import css from './MediaUploadBox.module.scss';
import { NewLibraryViaFileUploadModal } from './NewLibraryViaFileUploadModal';

import {
  isFacebookAdsMultipleData,
  isYoutubeChannel,
  isYoutubeMultipleData,
  isYoutubePlaylist,
} from '@lib/external/unfurl';
import { formatCreditsWithUnit } from '@lib/utils/units';
import {
  getAddAllButtonTooltipMessage,
  getCreditStatusMessage,
  getStorageStatusMessage,
  getURLStatusMessage,
} from '@lib/utils/urlPreupload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CircularProgress, Tooltip } from '@mui/material';
import { billingStore } from '@stores/Billing';
import { trackingStore } from '@stores/Tracking';
import { OVER_DURATION_LIMIT_CONTACT_MESSAGE } from '@stores/UploadURL/mediaStatus';
import { PreUploadStatusMessage } from './PreUploadStatusMessage';

// Every accepted extension, uppercased — tiled (angled) as a faint background
// texture in the drop zone for a bit of personality (decorative, aria-hidden).
// Deterministically shuffled (sorted by a stable string hash) so video/image/audio
// types are interleaved rather than grouped, while staying identical across
// renders. Repeated to fill the box; overflow is clipped.
const hashExtension = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
};

const ACCEPTED_TYPES_TILE = Array.from({ length: 40 })
  .fill(
    [
      ...ACCEPTED_VIDEO_FILE_EXTENSIONS,
      ...ACCEPTED_IMAGE_FILE_EXTENSIONS,
      ...ACCEPTED_AUDIO_FILE_EXTENSIONS,
    ]
      .map((ext) => ext.toUpperCase())
      .sort((a, b) => hashExtension(a) - hashExtension(b))
      .join('  '),
  )
  .join('  ');

interface MediaUploadBoxProps {
  className?: string;
  onUploadButtonClick: () => void;
  handleUploadAllVideosClick?: () => void;
  closeModal?: () => void;
  externalIsDragging?: boolean;
  isFirstTime?: boolean;
  /** Chosen destination is a brand-new library: create it on confirm. */
  createNewLibrary?: boolean;
  /** Name for the new library (entered next to the destination picker). */
  newLibraryName?: string;
  /** When set, the action button renders into this DOM node (the modal footer)
   *  via portal instead of inline. `null` = portal expected but not mounted yet. */
  footerPortalTarget?: HTMLElement | null;
  /** Gate run before a file pick/drop proceeds (e.g. require the new-library
   *  name). Returns false to abort. */
  validateBeforeProceed?: () => boolean;
  autoFocus?: boolean;
  compact?: boolean;
  /** The folder of the destination library uploads are filed in; its root when absent. */
  folderPath?: FolderPath;
}

export const MediaUploadBox = observer(
  ({
    className,
    onUploadButtonClick,
    handleUploadAllVideosClick,
    closeModal,
    externalIsDragging,
    isFirstTime = false,
    createNewLibrary = false,
    newLibraryName,
    footerPortalTarget,
    validateBeforeProceed,
    autoFocus = false,
    compact = false,
    folderPath = FOLDER_ROOT_PATH,
  }: MediaUploadBoxProps) => {
    // Create-new is driven by the destination picker ("+ New library"); isFirstTime
    // is the legacy auto-create-on-first-upload path. Either triggers library creation.
    const shouldCreateNewLibrary = createNewLibrary || isFirstTime;
    const [fileModalOpen, setFileModalOpen] = useState(false);
    const [supportedPlatformsTooltipOpen, setSupportedPlatformsTooltipOpen] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [droppedRejected, setDroppedRejected] = useState<DragDropRejected[]>([]);
    const droppedRejectedRef = useRef(droppedRejected);
    droppedRejectedRef.current = droppedRejected;
    const uploadManager = useUploadManager();
    const libraryFolder = useLibraryFolder();
    const libraryManager = useLibraryManager();
    const uploadURLStore = useUploadURL();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const urlInputRef = useRef<HTMLInputElement>(null);
    const creditsLeft = billingStore.creditsLeft;
    const libraryId = libraryFolder.library?.libraryId ?? 'unknown';
    const prevUrlCountRef = useRef(0);
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useIsMobile();

    const handleConfirmUpload = async (selectedFiles: File[], libraryName?: string) => {
      const rejects = droppedRejectedRef.current;
      try {
        if (shouldCreateNewLibrary) {
          const resolvedName = libraryName || newLibraryName || DEFAULT_LIBRARY_NAME;
          const newLibrary = await libraryManager.createNewLibrary(resolvedName);
          if (newLibrary) {
            navigate(getLibraryUrl(newLibrary._id, newLibrary.name));
            setSelectedFiles([]);
            setDroppedRejected([]);
            closeModal?.();
            await uploadManager.uploadFiles(
              selectedFiles,
              {
                libraryId: newLibrary._id,
                folderPath: FOLDER_ROOT_PATH,
              },
              'file-picker',
              rejects,
            );
            await billingStore.refetchCreditsAfterOperation();
          }
        } else {
          const libId = libraryFolder.library?.libraryId;
          const destination = libId ? { libraryId: libId, folderPath } : null;
          if (destination) {
            setSelectedFiles([]);
            setDroppedRejected([]);
            closeModal?.();
            // The destination may differ from the page we're on (the picker can
            // target any library), so navigate to it to show the upload landing.
            // Skip if we're already there, so we don't reset the library's URL
            // (and drop any active query params).
            if (!location.pathname.startsWith(`/library/${destination.libraryId}`)) {
              navigate(
                getLibraryUrl(destination.libraryId, libraryFolder.library?.data?.name || ''),
              );
            }
            uploadManager
              .uploadFiles(selectedFiles, destination, 'file-picker', rejects)
              .then(() => {
                uploadManager.refreshLibrary(destination.libraryId);
                billingStore.refetchCreditsAfterOperation();
              })
              .catch((error) => {
                console.error('Error uploading files:', error);
              });
          }
        }
      } catch (error) {
        console.error('Failed to upload files:', error);
      }
    };

    const fileUploadFlow = useFileUploadFlow({
      files: selectedFiles,
      onConfirm: handleConfirmUpload,
      onDismiss: () => {
        const unclaimed = droppedRejectedRef.current;
        if (unclaimed.length > 0) {
          uploadManager.rejectedFiles.push(
            ...unclaimed.map((r) => ({
              file: r.file,
              reason: r.reason,
              category: 'other' as const,
              status: 'failed' as const,
            })),
          );
        }
        setSelectedFiles([]);
        setDroppedRejected([]);
      },
      createNewLibrary: shouldCreateNewLibrary,
      newLibraryName,
    });

    // Trigger enhancement when URL becomes valid
    useEffect(() => {
      if (uploadURLStore.urlStatus === 'valid') {
        uploadURLStore.enhanceVideos();
      }
    }, [uploadURLStore.urlStatus, uploadURLStore.unfurlData]); // unfurlData is deprecated but kept for reactivity

    // Track URL validation results as they complete
    useEffect(() => {
      const dispose = reaction(
        () =>
          uploadURLStore.urlEntries.map((e) => ({
            id: e.id,
            url: e.url,
            status: e.status,
            errorMessage: e.errorMessage,
          })),
        (entries, prevEntries) => {
          const prevMap = new Map(prevEntries.map((e) => [e.id, e]));
          for (const entry of entries) {
            const prev = prevMap.get(entry.id);
            if (!prev || prev.status === entry.status) continue;
            if (entry.status !== 'valid' && entry.status !== 'error') continue;

            const isValid = entry.status === 'valid';
            const matchingEntry = uploadURLStore.urlEntries.find((e) => e.id === entry.id);
            const videos = matchingEntry?.enhancedVideos ?? [];
            const estimate = estimateIngest(videos);

            trackingStore.uploadModalUrlValidated({
              url: entry.url,
              libraryId,
              valid: isValid,
              errorReason: isValid ? undefined : entry.errorMessage,
              videoCount: isValid ? videos.length || uploadURLStore.totalVideoCount : undefined,
              totalDurationSeconds: isValid ? estimate.durationSeconds : undefined,
              creditsCost: isValid ? estimate.credits : undefined,
              creditsAvailable: billingStore.creditsLeft ?? undefined,
            });
          }
        },
      );
      return dispose;
    }, [libraryId]);

    const creditsCostInfo = uploadURLStore.getCreditsCostInfo(libraryFolder.uniqueIds, creditsLeft);
    const storageCostInfo = uploadURLStore.getStorageCostInfo(
      libraryFolder.uniqueIds,
      billingStore.storageLeft || 0,
    );
    const videoCountBreakdown = uploadURLStore.getVideoCountBreakdown(libraryFolder.uniqueIds);

    const hasUploadCost = creditsCostInfo !== null || storageCostInfo !== null;

    // The backend charges the library's `billingId`, so an upload into a library on another
    // billing account spends its credits and storage, not ours — see `resolveBillingOwnership`.
    // Note that is not the same as "somebody else owns it": org accounts share one billing
    // account, so a teammate's library is billed here. Both cost
    // figures above come from `billingStore` (this user's balance), so neither may gate the
    // button: `needsAction` drives both the disabled state and the "Insufficient credits"
    // tooltip below. The owner's balance is invisible to us; the backend rejects with 402.
    //
    // Suppressed while `shouldCreateNewLibrary` is set, matching the two upload flows: the target
    // library doesn't exist yet (it's created on confirm and billed to this user), so the library
    // this box happens to be mounted under says nothing about who pays.
    const billedToAnotherAccount =
      !shouldCreateNewLibrary && (libraryFolder.library?.isBilledToAnotherAccount ?? false);

    // Who to name, from the ownership axis. Gated on the flag so it inherits the same
    // `shouldCreateNewLibrary` carve-out. Left as its own value because the two axes can
    // disagree — an owner carrying neither a name nor an email, or a library billed elsewhere
    // that the reader owns — leaving this undefined with the flag still true, which the notice
    // words without a name.
    const ownerName = billedToAnotherAccount ? libraryFolder.library?.otherOwnerName : undefined;

    // A third probe because this is the one path that uploads without mounting either modal: a
    // single valid URL resolving to one media goes straight from this button. Above one media
    // the button only opens the picker, which probes the real selection instead.
    const ownerAffordability = useOwnerAffordability({
      libraryId: libraryFolder.library?.libraryId,
      ingestSeconds: storageCostInfo?.cost ?? 0,
      enabled: billedToAnotherAccount && uploadURLStore.videoCount <= 1,
    });
    // Same helper the confirm screens and `getUploadDisabledState` use, so this third surface
    // can't drift from the rule or its wording.
    const ownerBlock = ownerShortfallBlock(billedToAnotherAccount, ownerAffordability, ownerName);
    const ownerShortfall = ownerBlock?.shortfall;

    const topUp = useTopUp({
      creditCost: creditsCostInfo?.cost ?? 0,
      currentCredits: creditsLeft ?? 0,
      insufficient:
        !billedToAnotherAccount &&
        hasUploadCost &&
        !billingStore.hasEnoughCredits(creditsLeft, creditsCostInfo?.cost || 0),
      source: 'media-upload-box',
      // Only check storage when there's actually something to upload — otherwise
      // storageCostInfo is null and the fallback of 0 would incorrectly trigger insufficient.
      ...(hasUploadCost &&
        !billedToAnotherAccount && {
          storage: {
            afterProcessing: storageCostInfo?.left ?? 1,
            duration: storageCostInfo?.cost ?? 0,
          },
        }),
    });

    const creditStatusMessage =
      creditsCostInfo && creditsCostInfo.cost > 0
        ? getCreditStatusMessage({
            credits: creditsCostInfo,
            videoCount: videoCountBreakdown,
          })
        : null;

    const storageStatusMessage =
      storageCostInfo && storageCostInfo.cost > 0
        ? getStorageStatusMessage({
            storage: storageCostInfo,
            videoCount: videoCountBreakdown,
          })
        : null;

    const allVideosDuplicate = videoCountBreakdown.new === 0 && videoCountBreakdown.found > 0;

    const canAddAll =
      creditStatusMessage?.missingCredits === 0 && storageStatusMessage?.missingStorage === 0;

    const getUrlType = (): 'video' | 'playlist' | 'channel' | 'multiple' => {
      // If multiple valid URLs, return 'multiple'
      if (uploadURLStore.validEntries.length > 1) return 'multiple';

      const firstEntry = uploadURLStore.urlEntries[0];
      if (!firstEntry?.url) return 'video';

      // Check unfurlData first if available (more reliable)
      if (firstEntry.unfurlData) {
        if (isFacebookAdsMultipleData(firstEntry.unfurlData)) {
          return 'multiple'; // Facebook Ads Library is a collection of multiple items
        }
        if (isYoutubeMultipleData(firstEntry.unfurlData)) {
          if (isYoutubeChannel(firstEntry.unfurlData)) return 'channel';
          if (isYoutubePlaylist(firstEntry.unfurlData)) return 'playlist';
        }
      }
      // Only try URL parsing if URL looks valid (has protocol or is youtu.be)
      const url = firstEntry.url.trim();
      const looksLikeValidUrl = url.includes('://') || url.startsWith('youtu.be');
      if (!looksLikeValidUrl) {
        return 'video';
      }
      try {
        // Fallback to URL parsing - these functions will handle invalid URLs internally
        if (uploadURLStore.validationService.youtube.extractYouTubeChannelId(firstEntry.url))
          return 'channel';
        if (uploadURLStore.validationService.youtube.extractYouTubePlaylistId(firstEntry.url))
          return 'playlist';
        if (uploadURLStore.validationService.youtube.extractYouTubeVideoId(firstEntry.url))
          return 'video';
      } catch (error) {
        return 'video';
      }
      return 'video';
    };

    const urlStatusMessage = uploadURLStore.rawInput
      ? getURLStatusMessage({
          urlStatus: uploadURLStore.urlStatus,
          errorMessage: uploadURLStore.errorMessage,
          // Only pass video-related data when status is valid and we have the data
          videoCount:
            uploadURLStore.urlStatus === 'valid' && uploadURLStore.validEntries.length > 0
              ? uploadURLStore.videoCount
              : undefined,
          newVideoCount:
            uploadURLStore.urlStatus === 'valid' && uploadURLStore.validEntries.length > 0
              ? videoCountBreakdown.new
              : undefined,
          nonDuplicateEstimate:
            uploadURLStore.urlStatus === 'valid' && uploadURLStore.validEntries.length > 0
              ? uploadURLStore.getNonDuplicateEstimate(libraryFolder.uniqueIds)
              : undefined,
          urlType: getUrlType(),
          isUrlAmbiguous: uploadURLStore.isUrlAmbiguous,
          // Phase 3: show "Loading... X videos found" during SSE streaming
          loadingProgressCount:
            uploadURLStore.urlStatus === 'loading'
              ? uploadURLStore.urlEntries[0]?.loadingProgress?.loaded
              : undefined,
          mediaTitle:
            uploadURLStore.urlStatus === 'valid' && uploadURLStore.videoCount === 1
              ? uploadURLStore.allEnhancedVideos[0]?.title
              : undefined,
          mediaChannel:
            uploadURLStore.urlStatus === 'valid' && uploadURLStore.videoCount === 1
              ? uploadURLStore.allEnhancedVideos[0]?.channel?.name
              : undefined,
        })
      : null;

    // A single media item over the 10-hour limit is shown as an error up front
    // (red pill + disabled button) instead of being rejected after a click.
    // Mirrors the picker's 10-hour error tooltip wording.
    const overDurationLimit = uploadURLStore.overDurationLimit;
    const emptyGoogleDriveFolder = uploadURLStore.isEmptyGoogleDriveFolder;
    const effectiveUrlStatusMessage = emptyGoogleDriveFolder
      ? {
          message: 'This Google Drive folder appears to be empty.',
          status: 'warning' as const,
        }
      : overDurationLimit
        ? {
            message: (
              <>
                This media exceeds the 10-hour limit.{' '}
                <ContactUsLink message={OVER_DURATION_LIMIT_CONTACT_MESSAGE} /> if you need a higher
                limit.
              </>
            ),
            status: 'error' as const,
          }
        : urlStatusMessage;

    // Auto-focus the URL input when the component mounts if autoFocus is true.
    // Skipped on mobile: focusing the input pops the on-screen keyboard the
    // moment the add-media drawer/modal opens, which is jarring. Desktop only.
    useEffect(() => {
      if (autoFocus && !isMobile && urlInputRef.current) {
        urlInputRef.current.focus();
      }
    }, [autoFocus, isMobile]);

    const handleClickToUpload = () => {
      // Require a new-library name before opening the file picker (native validation).
      if (validateBeforeProceed && !validateBeforeProceed()) return;
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    };

    // True between picking files and the flow routing to the modal — covers our
    // processing + the modal mount (the browser building the FileList happens
    // before our handler runs, so it can't be covered). Used to show a disabled
    // "Loading your files..." state where the user just clicked. Scoped strictly
    // to 'pending' (the routing window) rather than "not selecting/confirming":
    // an error path can leave 'idle' with selectedFiles still set, and we must
    // not get stuck showing the loading state there.
    const filesLoading = fileUploadFlow.flowState === 'pending';

    const onFileExplorerChange = async (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const fileArray = Array.from(files);
        const totalSizeBytes = fileArray.reduce((sum, f) => sum + f.size, 0);
        trackingStore.uploadModalFilesSelected({
          libraryId,
          fileCount: fileArray.length,
          totalSizeBytes,
        });
        setSelectedFiles(fileArray);
        queueMicrotask(() => fileUploadFlow.open());
        return;
      }
    };

    const handleFilesDropped = useCallback(
      (droppedFiles: File[], rejected: DragDropRejected[]) => {
        // Require a new-library name before accepting a drop (native validation).
        if (validateBeforeProceed && !validateBeforeProceed()) return;
        const totalSizeBytes = droppedFiles.reduce((sum, f) => sum + f.size, 0);
        trackingStore.uploadModalFilesDropped({
          libraryId,
          fileCount: droppedFiles.length,
          totalSizeBytes,
        });
        setSelectedFiles(droppedFiles);
        if (rejected.length > 0) {
          setDroppedRejected((prev) => [...prev, ...rejected]);
        }
        queueMicrotask(() => fileUploadFlow.open());
      },
      [libraryId, fileUploadFlow.open, validateBeforeProceed],
    );

    // Drag and drop functionality
    const { isDragging } = useDragDropUpload(
      false,
      undefined, // onDragEnter
      undefined, // onDragLeave, remove the CSS class from the parent element
      undefined, // onDrop
      handleFilesDropped,
    );

    const handleCloseFileModal = () => {
      setFileModalOpen(false);
      setSelectedFiles([]);
    };

    // Use external dragging state or internal dragging state for styling
    const isCurrentlyDragging = externalIsDragging ?? isDragging ?? false;

    const addAllButtonEnabled =
      creditStatusMessage && storageStatusMessage
        ? getAddAllButtonTooltipMessage({
            creditStatusMessage,
            storageStatusMessage,
          })
        : { value: false, reason: '' };

    const urlType = uploadURLStore.urlType;

    // The action button. On mobile it renders into the modal's footer slot (via
    // portal — its React tree, and thus uploadURLStore context, is preserved) so
    // it reads as a real footer; on desktop / other surfaces it renders inline.
    const uploadButtonNode = (
      <div className={css.uploadButtonContainer}>
        {uploadURLStore.videoCount > 1 ? (
          // Deliberately NOT gated on `ownerShortfall`: this opens the picker, and a shortfall
          // priced against the whole URL says nothing about the subset the user will pick. The
          // picker re-probes and gates Add there.
          <span>
            <ActionButton
              className={css.chooseVideosButton}
              variant="primary"
              fullWidth
              onClick={onUploadButtonClick}
              disabled={
                uploadURLStore.urlStatus !== 'valid' ||
                uploadURLStore.loadingEntries.length > 0 ||
                uploadURLStore.isAddingUrl ||
                allVideosDuplicate
              }
            >
              <span>Choose media to add</span>
            </ActionButton>
          </span>
        ) : (
          <>
            {/* Over the duration limit takes priority over a credits/storage
                top-up: topping up won't help a too-long video, so suppress
                the top-up CTA and just show the disabled button + red pill. */}
            {topUp.needsAction && topUp.buttonLabel && !overDurationLimit && (
              <Tooltip title={topUp.tooltip ?? ''} placement="top" arrow>
                <span>
                  <ActionButton
                    fullWidth
                    onClick={topUp.handleTopUp}
                    disabled={topUp.isToppingUp}
                    padding={64}
                  >
                    <span>{topUp.buttonLabel}</span>
                  </ActionButton>
                </span>
              </Tooltip>
            )}
            <Tooltip
              title={
                overDurationLimit
                  ? 'This media exceeds the 10-hour limit.'
                  : ownerBlock
                    ? ownerBlock.reason
                    : topUp.needsAction
                      ? topUp.isStoragePriority
                        ? 'Not enough storage.'
                        : `Insufficient credits. You need ${formatCreditsWithUnit(creditsCostInfo?.cost || 0, 'up')} but only have ${formatCreditsWithUnit(creditsLeft, 'down')} remaining.`
                      : allVideosDuplicate
                        ? 'No need to add media, as all media found in this URL are already in your library.'
                        : ''
              }
              placement="top"
              arrow
            >
              <span>
                <ActionButton
                  className={css.uploadButton}
                  variant={topUp.needsAction ? 'secondary' : undefined}
                  fullWidth
                  onClick={onUploadButtonClick}
                  disabled={
                    uploadURLStore.urlStatus !== 'valid' ||
                    uploadURLStore.isAddingUrl ||
                    topUp.needsAction ||
                    allVideosDuplicate ||
                    overDurationLimit ||
                    emptyGoogleDriveFolder ||
                    !!ownerShortfall
                  }
                  padding={64}
                >
                  <span>{uploadURLStore.isAddingUrl ? 'Adding...' : 'Add media'}</span>
                </ActionButton>
              </span>
            </Tooltip>
          </>
        )}
      </div>
    );

    // undefined → render inline (desktop / non-portal surfaces); an element →
    // portal into the footer; null → portal target not mounted yet, render nothing.
    const renderedUploadButton =
      footerPortalTarget === undefined
        ? uploadButtonNode
        : footerPortalTarget
          ? createPortal(uploadButtonNode, footerPortalTarget)
          : null;

    return (
      <div
        className={classNames(css.uploadArea, className, {
          [css.dragging]: isCurrentlyDragging,
          [css.compact]: compact,
        })}
      >
        <div
          className={classNames(css.topPart, { [css.topPartLoading]: filesLoading })}
          onClick={filesLoading ? undefined : handleClickToUpload}
          aria-disabled={filesLoading}
        >
          <div className={css.fileTypesTile} aria-hidden="true">
            {ACCEPTED_TYPES_TILE}
          </div>
          {filesLoading ? (
            <>
              <div className={css.uploadIcon}>
                <CircularProgress size={24} sx={{ color: 'inherit' }} />
              </div>
              <div className={css.clickToUpload}>
                <span>Loading your files...</span>
              </div>
            </>
          ) : (
            <>
              <div className={css.uploadIcon}>
                <UploadDocumentsIcon />
              </div>
              <div className={css.clickToUpload}>
                <span className={css.desktopText}>Drag files here or click to upload</span>
                <span className={css.mobileText}>Tap to upload files</span>
              </div>
            </>
          )}
          <input
            id="file"
            type="file"
            hidden
            ref={fileInputRef}
            accept={getMediaAcceptAttribute()}
            onChange={onFileExplorerChange}
            multiple
          />
        </div>
        <div className={css.or}>
          <hr />
          <span>OR</span>
          <hr />
        </div>
        <div className={css.bottomPart}>
          <URLInput
            ref={urlInputRef}
            placeholder={
              isMobile
                ? 'Paste one or more media links to add.'
                : 'Paste one or more media links (separated by spaces)'
            }
            id="mediaUrl"
            value={uploadURLStore.rawInput}
            onChange={(e) => {
              const prevCount = prevUrlCountRef.current;
              uploadURLStore.setRawInput(e.target.value);
              const newCount = uploadURLStore.urlEntries.length;
              if (newCount > prevCount && newCount > 0) {
                const urls = uploadURLStore.urlEntries.map((entry) => entry.url);
                trackingStore.uploadModalLinkPasted({ libraryId, urlCount: newCount, urls });
              }
              prevUrlCountRef.current = newCount;
            }}
            className={css.urlInput}
            status={uploadURLStore.rawInput ? uploadURLStore.urlStatus : undefined}
          />
          {/* Multi-URL summary when there are multiple URLs */}
          {uploadURLStore.urlEntries.length > 1 && (
            <PreUploadStatusMessage
              status={
                uploadURLStore.overallStatus === 'allValid'
                  ? 'valid'
                  : uploadURLStore.overallStatus === 'allError'
                    ? 'error'
                    : uploadURLStore.overallStatus === 'loading'
                      ? 'loading'
                      : 'warning'
              }
              className={compact ? css.compactStatusMessage : undefined}
            >
              {uploadURLStore.urlEntries.length} URLs:{' '}
              {uploadURLStore.validEntries.length > 0 &&
                `${uploadURLStore.validEntries.length} valid`}
              {uploadURLStore.loadingEntries.length > 0 &&
                (() => {
                  const loadingCount = uploadURLStore.loadingEntries.length;
                  const videosFound = uploadURLStore.loadingEntries.reduce(
                    (sum, e) => sum + (e.loadingProgress?.loaded ?? 0),
                    0,
                  );
                  const suffix = videosFound > 0 ? ` (${videosFound} media found)` : '';
                  return `${uploadURLStore.validEntries.length > 0 ? ', ' : ''}${loadingCount} loading${suffix}`;
                })()}
              {uploadURLStore.errorEntries.length > 0 &&
                `${uploadURLStore.validEntries.length > 0 || uploadURLStore.loadingEntries.length > 0 ? ', ' : ''}${uploadURLStore.errorEntries.length} failed`}
            </PreUploadStatusMessage>
          )}
          {/* Single URL status message (or first URL when multiple) */}
          {uploadURLStore.urlEntries.length === 1 &&
            effectiveUrlStatusMessage &&
            effectiveUrlStatusMessage.message &&
            effectiveUrlStatusMessage.status &&
            !allVideosDuplicate && (
              <PreUploadStatusMessage
                status={effectiveUrlStatusMessage.status}
                className={compact ? css.compactStatusMessage : undefined}
                info={
                  effectiveUrlStatusMessage.status === 'valid' && uploadURLStore.videoCount === 1
                    ? 'Has one media item'
                    : undefined
                }
                impactData={
                  effectiveUrlStatusMessage.status === 'valid' && creditsCostInfo && storageCostInfo
                    ? {
                        credits: creditsCostInfo,
                        storage: storageCostInfo,
                      }
                    : undefined
                }
                billedToAnotherAccount={billedToAnotherAccount}
                ownerName={ownerName}
                shortfall={ownerShortfall}
              >
                {effectiveUrlStatusMessage.message}
              </PreUploadStatusMessage>
            )}
          {allVideosDuplicate && (
            <PreUploadStatusMessage
              status="valid"
              className={compact ? css.compactStatusMessage : undefined}
              info={uploadURLStore.videoCount === 1 ? 'Has one media item' : undefined}
            >
              All media found in this URL are already in your library.
            </PreUploadStatusMessage>
          )}
          {uploadURLStore.urlStatus !== 'valid' &&
            uploadURLStore.urlStatus !== 'loading' &&
            !uploadURLStore.isAnyEnhancing &&
            (!compact || !uploadURLStore.rawInput) &&
            (compact ? (
              <div
                className={css.compactSupportedPlatforms}
                onClick={() => setSupportedPlatformsTooltipOpen((prev) => !prev)}
              >
                <span>Supported platforms</span>
                <Tooltip
                  title={
                    <span>
                      We support media from YouTube, Vimeo, Facebook, Google Drive, Frame.io,
                      Dropbox, Box, Shade, LinkedIn, TikTok, and SoundCloud.
                    </span>
                  }
                  placement="top"
                  arrow
                  open={supportedPlatformsTooltipOpen}
                  onClose={() => setSupportedPlatformsTooltipOpen(false)}
                  disableTouchListener
                >
                  <InfoOutlinedIcon className={css.compactInfoIcon} />
                </Tooltip>
              </div>
            ) : (
              <PreUploadStatusMessage status="neutral">
                <SupportedPlatformsText />
              </PreUploadStatusMessage>
            ))}
          {/*
            Persistent cross-billing disclosure. The `ImpactPopup` also carries it, but that
            popup only exists while the cursor sits on the info icon — and the single-URL path
            (`handleUploadButtonClick` → `handleUploadUrl` when there's exactly one valid entry
            and one media) uploads straight away, mounting neither `ChooseMediaModal` nor its
            confirm screen. So on a hoverless device that popup was the *only* disclosure, and
            it was unreachable: the user would spend someone else's credits with no indication.
            Hover and width are independent axes (see `.claude/rules/mobile-breakpoints.md`), so
            this can't be a `@include mobile` branch — it has to be here unconditionally.
          */}
          {billedToAnotherAccount && (
            <BilledToOwnerNotice
              ownerName={ownerName}
              shortfall={ownerShortfall}
              className={css.billedToOwnerNotice}
            />
          )}
        </div>
        {renderedUploadButton}
        {fileUploadFlow.flowState === 'selecting' && (
          <ChooseFileUploadModal {...fileUploadFlow.selectionProps} open={true} />
        )}
        <ChooseFileUploadModalSummaryConfirm {...fileUploadFlow.summaryProps} />
        <NewLibraryViaFileUploadModal
          open={fileModalOpen}
          onClose={handleCloseFileModal}
          files={selectedFiles}
        />
      </div>
    );
  },
);
